import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import StatusPill from './StatusPill'
import { supabase } from '../lib/supabase'
import JobEvidence from './JobEvidence'
import {
  canCreateAdditionalWorkFromChecklistItem,
} from '../lib/generalAdditionalWork'
import {
  canCreateExtraServiceLineFromChecklistItem,
} from '../lib/generalExtraServiceLines'
import {
  allowedChecklistResults,
  checklistItemForm,
  checklistCardViews,
  checklistProgress,
  checklistResultNeedsComment,
  checklistResultNeedsDetails,
  checklistResultLabel,
  checklistStageLabel,
  checklistStageQuickResult,
  createChecklistOverride,
  ensureGeneralJobChecklists,
  hasCurrentChecklistOverride,
  loadGeneralJobChecklists,
  mergeChecklistDraftForms,
  readyForCollectionBlockers,
  updateChecklistItem,
  validateChecklistItemInput,
} from '../lib/generalChecklists'

function stageEvidenceStage(stage) {
  if (stage === 'pre_service_inspection') return 'inspection'
  if (stage === 'final_qc') return 'qc'
  return 'service'
}

function stageSaveLabel(stage) {
  if (stage === 'pre_service_inspection') return 'Pre-service'
  return checklistStageLabel(stage)
}

function displayDateTime(value) {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return String(value)
  return date.toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' })
}

function itemStatusTone(item) {
  if (item.result === 'pending') return 'muted'
  if (item.result === 'fail' || item.result === 'action_required' || item.result === 'unable_to_complete') return 'orange'
  if (item.result === 'additional_work_required' || item.result === 'advisory') return 'blue'
  return 'green'
}

function ProgressChip({ progress }) {
  const suffix = progress.stage === 'final_qc' && progress.done ? ' passed' : ''
  const tone = progress.blockers.length ? 'orange' : progress.done ? 'green' : progress.complete ? 'blue' : 'muted'
  return (
    <StatusPill tone={tone}>
      {progress.label}: {progress.completeLabel}{suffix}
    </StatusPill>
  )
}

export default function GeneralChecklistPanel({
  jobId,
  workshopId,
  mode = 'edit',
  canOverrideQc = false,
  onCreateAdditionalWork,
  onCreateExtraServiceLine,
  onChanged,
  onSummary,
}) {
  const editable = mode === 'edit'
  const [checklists, setChecklists] = useState([])
  const [forms, setForms] = useState({})
  const [dirtyItemIds, setDirtyItemIds] = useState(() => new Set())
  const dirtyItemIdsRef = useRef(new Set())
  const [expandedItemIds, setExpandedItemIds] = useState(() => new Set())
  const [overrideReasons, setOverrideReasons] = useState({})
  const [openEvidenceItemId, setOpenEvidenceItemId] = useState('')
  const [loading, setLoading] = useState(false)
  const [savingStage, setSavingStage] = useState('')
  const [overridingItemId, setOverridingItemId] = useState('')
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')
  const [missingSchema, setMissingSchema] = useState(false)

  const load = useCallback(async () => {
    if (!jobId || !workshopId) {
      setChecklists([])
      setForms({})
      dirtyItemIdsRef.current = new Set()
      setDirtyItemIds(new Set())
      return
    }

    setLoading(true)
    setError('')
    setNotice('')
    try {
      const ensured = await ensureGeneralJobChecklists(supabase, jobId)
      setMissingSchema(Boolean(ensured.missingSchema))
      const rows = ensured.missingSchema
        ? []
        : await loadGeneralJobChecklists(supabase, { jobId, workshopId })
      setChecklists(rows)
      setForms(current => mergeChecklistDraftForms(rows, current, dirtyItemIdsRef.current))
    } catch (err) {
      setError(err.message || 'Could not load General checklist.')
      setChecklists([])
      setForms({})
      dirtyItemIdsRef.current = new Set()
      setDirtyItemIds(new Set())
    } finally {
      setLoading(false)
    }
  }, [jobId, workshopId])

  useEffect(() => {
    dirtyItemIdsRef.current = new Set()
    setDirtyItemIds(new Set())
    setExpandedItemIds(new Set())
    setOpenEvidenceItemId('')
  }, [jobId, workshopId])

  useEffect(() => {
    load()
  }, [load])

  const progress = useMemo(() => checklistProgress(checklists), [checklists])
  const cardViews = useMemo(() => checklistCardViews(checklists), [checklists])
  const blockers = useMemo(() => readyForCollectionBlockers(checklists), [checklists])

  useEffect(() => {
    if (onSummary) {
      onSummary({
        hasChecklists: checklists.length > 0,
        progress,
        blockers,
      })
    }
  }, [checklists, progress, blockers, onSummary])

  function updateDirtyItemIds(updater) {
    const next = updater(new Set(dirtyItemIdsRef.current))
    dirtyItemIdsRef.current = next
    setDirtyItemIds(next)
  }

  function markItemsDirty(itemIds = []) {
    updateDirtyItemIds(current => {
      itemIds.forEach(itemId => current.add(itemId))
      return current
    })
  }

  function clearItemsDirty(itemIds = []) {
    updateDirtyItemIds(current => {
      itemIds.forEach(itemId => current.delete(itemId))
      return current
    })
  }

  function toggleItemDetails(itemId) {
    setExpandedItemIds(current => {
      const next = new Set(current)
      if (next.has(itemId)) next.delete(itemId)
      else next.add(itemId)
      return next
    })
  }

  function setItemField(item, key, value) {
    const itemId = item.id
    setForms(current => ({
      ...current,
      [itemId]: { ...checklistItemForm(item), ...(current[itemId] || {}), [key]: value },
    }))
    markItemsDirty([itemId])
    setNotice('')
    if (key === 'result' && checklistResultNeedsDetails(item.stage, value)) {
      setExpandedItemIds(current => {
        const next = new Set(current)
        next.add(itemId)
        return next
      })
    }
  }

  function markRemaining(stage, stageItems = []) {
    const result = checklistStageQuickResult(stage)
    const targetItems = stageItems.filter(item => {
      const form = forms[item.id] || checklistItemForm(item)
      return (form.result || item.result || 'pending') === 'pending'
    })

    if (!targetItems.length) {
      setError('')
      setNotice(`No pending ${checklistStageLabel(stage).toLowerCase()} items to update.`)
      return
    }

    setForms(current => {
      const next = { ...current }
      targetItems.forEach(item => {
        const form = next[item.id] || checklistItemForm(item)
        next[item.id] = { ...form, result }
      })
      return next
    })
    markItemsDirty(targetItems.map(item => item.id))
    setError('')
    setNotice(`${targetItems.length} item${targetItems.length === 1 ? '' : 's'} marked ${checklistResultLabel(stage, result)}. Save the stage to keep the change.`)
  }

  async function saveStage(stage, stageItems = [], saveKey = stage) {
    const dirtyItems = stageItems.filter(item => dirtyItemIds.has(item.id))
    if (!dirtyItems.length) {
      setError('')
      setNotice(`No ${checklistStageLabel(stage).toLowerCase()} changes to save.`)
      return
    }

    const saves = dirtyItems.map(item => {
      const form = forms[item.id] || checklistItemForm(item)
      return {
        item,
        form,
        validation: validateChecklistItemInput({ ...item, ...form }),
      }
    })
    const invalid = saves.find(row => !row.validation.valid)
    if (invalid) {
      setError(`${invalid.item.label}: ${invalid.validation.message}`)
      if (checklistResultNeedsDetails(invalid.item.stage, invalid.form.result)) {
        setExpandedItemIds(current => {
          const next = new Set(current)
          next.add(invalid.item.id)
          return next
        })
      }
      return
    }

    setSavingStage(saveKey)
    setError('')
    setNotice('')
    try {
      await Promise.all(saves.map(({ item, form }) => updateChecklistItem(supabase, item, form)))
      clearItemsDirty(dirtyItems.map(item => item.id))
      await load()
      setNotice(`${checklistStageLabel(stage)} saved.`)
      if (onChanged) onChanged()
    } catch (err) {
      setError(err.message || `Could not save ${checklistStageLabel(stage).toLowerCase()}.`)
    } finally {
      setSavingStage('')
    }
  }

  async function authoriseOverride(item) {
    const reason = overrideReasons[item.id] || ''
    setOverridingItemId(item.id)
    setError('')
    setNotice('')
    try {
      await createChecklistOverride(supabase, item, reason)
      setOverrideReasons(current => ({ ...current, [item.id]: '' }))
      await load()
      setNotice('Final QC override recorded.')
      if (onChanged) onChanged()
    } catch (err) {
      setError(err.message || 'Could not record Final QC override.')
    } finally {
      setOverridingItemId('')
    }
  }

  if (!jobId) {
    return (
      <>
        <div className="sec">General Service Checklist</div>
        <div className="job-modal-callout" style={{ marginBottom: 14 }}>
          <div className="fs12 text-muted">Save the General job before checklist items can be created.</div>
        </div>
      </>
    )
  }

  return (
    <>
      <div className="sec">General Service Checklist</div>
      <div className="job-modal-callout" style={{ marginBottom: 14 }}>
        <div className="section-head compact" style={{ marginBottom: 10 }}>
          <div>
            <div className="job-modal-callout-title">General Service Cards</div>
            <div className="text-muted fs12">Created from explicitly assigned service processes. Internal notes stay private; customer-visible comments can appear in General reports.</div>
          </div>
          <span className="job-status-chip job-status-blue">General only</span>
        </div>

        {error && <div className="alert alert-error" style={{ marginBottom: 10 }}>{error}</div>}
        {notice && <div className="alert alert-success" style={{ marginBottom: 10 }}>{notice}</div>}
        {loading && <div className="fs12 text-muted">Loading General checklist...</div>}
        {missingSchema && <div className="alert alert-info">Checklist schema v80 has not been applied yet.</div>}

        {!loading && !missingSchema && checklists.length === 0 && (
          <div className="muted-empty">No Service Card is assigned to this General job. Configure each selected service in Workshop Settings.</div>
        )}

        {checklists.length > 0 && (
          <>
            <div className="action-row" style={{ justifyContent: 'flex-start', gap: 6, marginBottom: 12 }}>
              {progress.map(row => <ProgressChip key={row.stage} progress={row} />)}
              <StatusPill tone={blockers.length ? 'orange' : 'green'}>
                Report: {blockers.length ? 'QC blocked' : 'ready'}
              </StatusPill>
            </div>

            {blockers.length > 0 && (
              <div className="alert alert-info" style={{ marginBottom: 12 }}>
                Ready for Collection is blocked until mandatory Final QC is completed or a workshop owner override is recorded for failed checks.
              </div>
            )}

            {cardViews.map(card => (
              <section key={card.id} style={{ borderTop: '1px solid var(--border)', paddingTop: 12, marginBottom: 16 }}>
                <div className="section-head compact" style={{ marginBottom: 10 }}>
                  <div>
                    <div className="job-modal-callout-title">{card.title}</div>
                    <div className="fs12 text-muted">
                      Version {card.version} · {card.serviceNames.join(', ') || 'Assigned General service'}
                    </div>
                  </div>
                  <div className="action-row" style={{ justifyContent: 'flex-end', gap: 6 }}>
                    {card.stages.map(row => <ProgressChip key={row.stage} progress={row.progress} />)}
                  </div>
                </div>

                {card.stages.map(({ stage, label, items: stageItems, progress: stageMeta }) => {
                  if (!stageItems.length) return null
                  const dirtyCount = stageItems.filter(item => dirtyItemIds.has(item.id)).length
                  const quickResult = checklistStageQuickResult(stage)
                  const stageSaveKey = `${card.id}:${stage}`
                  const stageSaving = savingStage === stageSaveKey
                  return (
                    <details key={stageSaveKey} open className="general-checklist-stage" style={{ border: '1px solid var(--border)', borderRadius: 6, padding: 12, marginBottom: 10, background: 'var(--black)' }}>
                  <summary className="general-checklist-stage-summary">
                    <span>
                      {label} <span className="text-muted fs12">({stageMeta?.completeLabel || `0/${stageItems.length}`})</span>
                    </span>
                    {dirtyCount > 0 && <span className="job-status-chip job-status-blue">{dirtyCount} unsaved</span>}
                  </summary>
                  {editable && (
                    <div className="general-checklist-stage-actions">
                      <button
                        type="button"
                        className="btn btn-ghost btn-sm"
                        onClick={() => markRemaining(stage, stageItems)}
                        disabled={stageSaving}
                      >
                        Mark remaining {checklistResultLabel(stage, quickResult)}
                      </button>
                      <button
                        type="button"
                        className="btn btn-primary btn-sm"
                        onClick={() => saveStage(stage, stageItems, stageSaveKey)}
                        disabled={stageSaving || dirtyCount === 0}
                      >
                        {stageSaving ? 'Saving...' : `Save ${stageSaveLabel(stage)}`}
                      </button>
                    </div>
                  )}
                  <div style={{ display: 'grid', gap: 10, marginTop: 12 }}>
                    {stageItems.map(item => {
                      const form = forms[item.id] || checklistItemForm(item)
                      const displayItem = { ...item, result: form.result }
                      const detailsOpen = expandedItemIds.has(item.id) || checklistResultNeedsDetails(item.stage, form.result)
                      const blocking = blockers.some(row => row.id === item.id)
                      const currentOverride = hasCurrentChecklistOverride(item)
                      const showOverride = editable
                        && canOverrideQc
                        && item.stage === 'final_qc'
                        && item.required === true
                        && item.failure_blocks_completion !== false
                        && item.result === 'fail'
                        && !currentOverride
                      const dirty = dirtyItemIds.has(item.id)
                      const canStartAdditionalWork = editable
                        && typeof onCreateAdditionalWork === 'function'
                        && canCreateAdditionalWorkFromChecklistItem({ ...item, result: form.result })
                      const canStartExtraServiceLine = editable
                        && typeof onCreateExtraServiceLine === 'function'
                        && canCreateExtraServiceLineFromChecklistItem({ ...item, result: form.result })
                      return (
                        <div key={item.id} className="muted-empty" style={{ textAlign: 'left', borderStyle: 'solid' }}>
                          <div className="section-head compact" style={{ marginBottom: 8 }}>
                            <div style={{ minWidth: 0 }}>
                              <div className="text-white fw7">{item.label}</div>
                              {item.technician_guidance && <div className="fs12 text-muted" style={{ marginTop: 3 }}>{item.technician_guidance}</div>}
                              <div className="action-row" style={{ justifyContent: 'flex-start', gap: 6, marginTop: 6 }}>
                                <StatusPill tone={itemStatusTone(displayItem)}>{checklistResultLabel(item.stage, form.result)}</StatusPill>
                                {dirty && <span className="job-status-chip job-status-blue">Unsaved</span>}
                                {item.required && <span className="job-status-chip job-status-muted">Required</span>}
                                {item.failure_blocks_completion && <StatusPill tone="orange">QC gate</StatusPill>}
                                {currentOverride && <StatusPill tone="blue">Override authorised</StatusPill>}
                                {blocking && <StatusPill tone="orange">Blocks Ready</StatusPill>}
                              </div>
                            </div>
                          </div>

                          {editable ? (
                            <>
                              <div className="checklist-result-options" role="radiogroup" aria-label={`${item.label} result`}>
                                {allowedChecklistResults(item.stage).map(([value, resultLabel]) => {
                                  const selected = form.result === value
                                  const intervention = checklistResultNeedsDetails(item.stage, value)
                                  return (
                                    <button
                                      key={value}
                                      type="button"
                                      role="radio"
                                      className={`checklist-result-choice ${selected ? 'active' : ''} ${intervention ? 'intervention' : ''}`}
                                      onClick={() => setItemField(item, 'result', value)}
                                      disabled={stageSaving}
                                      aria-checked={selected}
                                    >
                                      <span className="checklist-result-tick" aria-hidden="true">{selected ? '✓' : ''}</span>
                                      {resultLabel}
                                    </button>
                                  )
                                })}
                              </div>
                              <div className="action-row checklist-item-meta-row">
                                <span className="fs12 text-muted">
                                  {item.completed_at ? `Completed by ${item.completed_by_name || 'technician'} on ${displayDateTime(item.completed_at)}` : 'Not completed yet'}
                                </span>
                                {checklistResultNeedsComment(item.stage, form.result) && <StatusPill tone="orange">Comment required</StatusPill>}
                                <button type="button" className="btn btn-ghost btn-sm" onClick={() => toggleItemDetails(item.id)} disabled={stageSaving}>
                                  {detailsOpen ? 'Hide Notes' : 'Notes'}
                                </button>
                              </div>
                              {detailsOpen && (
                                <div className="checklist-item-details">
                                  <div className="field">
                                    <label>Customer-facing comment{checklistResultNeedsComment(item.stage, form.result) ? ' required' : ''}</label>
                                    <input
                                      value={form.customer_comment}
                                      onChange={e => setItemField(item, 'customer_comment', e.target.value)}
                                      placeholder="Customer-safe comment for report, if needed."
                                      disabled={stageSaving}
                                    />
                                  </div>
                                  <div className="field">
                                    <label>Internal technician note</label>
                                    <textarea
                                      value={form.internal_note}
                                      onChange={e => setItemField(item, 'internal_note', e.target.value)}
                                      placeholder="Workshop-only note."
                                      disabled={stageSaving}
                                    />
                                  </div>
                                </div>
                              )}
                            </>
                          ) : (
                            <div style={{ display: 'grid', gap: 6 }}>
                              {item.customer_comment && <div className="fs12"><span className="text-green fw7">Customer comment: </span>{item.customer_comment}</div>}
                              {item.internal_note && <div className="fs12 text-muted" style={{ whiteSpace: 'pre-wrap' }}>Internal note: {item.internal_note}</div>}
                              <div className="fs12 text-muted">
                                {item.completed_at ? `Completed by ${item.completed_by_name || 'technician'} on ${displayDateTime(item.completed_at)}` : 'Not completed yet'}
                              </div>
                            </div>
                          )}

                          {showOverride && (
                            <div className="job-modal-callout" style={{ marginTop: 10 }}>
                              <div className="job-modal-callout-title">Workshop owner Final QC override</div>
                              <div className="field" style={{ marginTop: 8 }}>
                                <label>Override reason</label>
                                <textarea
                                  value={overrideReasons[item.id] || ''}
                                  onChange={e => setOverrideReasons(current => ({ ...current, [item.id]: e.target.value }))}
                                  placeholder="Explain why this failed mandatory QC item is being overridden."
                                  style={{ minHeight: 62 }}
                                  disabled={overridingItemId === item.id}
                                />
                              </div>
                              <button className="btn btn-primary btn-sm" onClick={() => authoriseOverride(item)} disabled={overridingItemId === item.id}>
                                {overridingItemId === item.id ? 'Recording...' : 'Authorise Override'}
                              </button>
                            </div>
                          )}

                          <div className="action-row" style={{ justifyContent: 'flex-start', marginTop: 10 }}>
                            {canStartAdditionalWork && (
                              <button
                                type="button"
                                className="btn btn-primary btn-sm"
                                onClick={() => onCreateAdditionalWork({
                                  source_type: 'checklist_item',
                                  checklist_item_id: item.id,
                                  title: item.label,
                                  customer_explanation: form.customer_comment,
                                  internal_note: form.internal_note,
                                })}
                                disabled={dirty || stageSaving}
                                title={dirty ? 'Save this checklist stage before creating additional work.' : ''}
                              >
                                Create Additional Work
                              </button>
                            )}
                            {canStartExtraServiceLine && (
                              <button
                                type="button"
                                className="btn btn-ghost btn-sm"
                                onClick={() => onCreateExtraServiceLine({
                                  source_type: 'checklist_item',
                                  checklist_item_id: item.id,
                                  customer_explanation: form.customer_comment,
                                  internal_note: form.internal_note,
                                })}
                                disabled={dirty || stageSaving}
                                title={dirty ? 'Save this checklist stage before requesting an extra service.' : ''}
                              >
                                Request Extra Service
                              </button>
                            )}
                            <button
                              type="button"
                              className="btn btn-ghost btn-sm"
                              onClick={() => setOpenEvidenceItemId(openEvidenceItemId === item.id ? '' : item.id)}
                            >
                              {openEvidenceItemId === item.id ? 'Hide Evidence' : 'Attach Evidence'}
                            </button>
                          </div>
                          {openEvidenceItemId === item.id && (
                            <div style={{ marginTop: 10 }}>
                              <JobEvidence
                                jobId={jobId}
                                workshopId={workshopId}
                                mode={editable ? 'edit' : 'view'}
                                context="general"
                                checklistItemId={item.id}
                                defaultStage={stageEvidenceStage(item.stage)}
                                lockStage
                                compact
                                onChanged={onChanged}
                              />
                            </div>
                          )}
                        </div>
                      )
                    })}
                  </div>
                    </details>
                  )
                })}
              </section>
            ))}
          </>
        )}
      </div>
    </>
  )
}
